import { useState, useEffect, useCallback } from 'react'
import useConversationsStore from '@/stores/useConversationsStore'
import { mapServerMessageToClientMessage } from '../services/conversations-mapper'
import { Message as ClientMessage, ServerMessage } from '@/routes/plus/models/Conversation.model'
import { useAwsCredentialsManager } from '../context/AWS-credentials.context'
import { getConfig } from '@/config/runtime'

type MessagesApiResponse = {
  messages: ServerMessage[]
  nextToken: string | null
}


interface UseConversationMessagesLoaderProps {
  conversationId: string | null
}

interface UseConversationMessagesLoaderReturn {
  isLoading: boolean
  error: Error | null
  fetchMessagesForCurrentId: (convId: string) => Promise<void>
}

export const useMessagesFetcher = ({
  conversationId,
}: UseConversationMessagesLoaderProps): UseConversationMessagesLoaderReturn => {
  const getConversation = useConversationsStore((state) => state.getConversation)
  const setMessagesInStore = useConversationsStore((state) => state.setMessages)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const [fetchAttemptedForId, setFetchAttemptedForId] = useState<string | null>(null)

  const {
    applicationId,
    getValidCredentials,
    isLoading: credentialsLoading,
  } = useAwsCredentialsManager()

  const fetchMessages = useCallback(
    async (convId: string) => {
      if (isLoading) return

      if (credentialsLoading) {
        console.warn(`WorkspaceMessages (${convId}): AWS credentials loading. Deferring.`)
        return
      }

      const fetchedCredentials = await getValidCredentials()

      if (
        !fetchedCredentials?.AccessKeyId ||
        !fetchedCredentials.SecretAccessKey ||
        !fetchedCredentials.SessionToken ||
        !applicationId
      ) {
        const credsError = new Error(
          'AWS credentials or Application ID are not available. Cannot fetch messages.'
        )
        setError(credsError)
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const response = await fetch(`${getConfig().plus.apiUrl}/list-messages`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            accessKeyId: fetchedCredentials.AccessKeyId,
            secretAccessKey: fetchedCredentials.SecretAccessKey,
            sessionToken: fetchedCredentials.SessionToken,
            applicationId: applicationId,
            conversationId: convId,
            maxResults: 10,
            nextToken: null,
          }),
        })

        if (!response.ok) {
          let errorBody = `API request failed with status ${response.status}`
          try {
            const errData = await response.json()
            errorBody = errData.message || JSON.stringify(errData) || errorBody
          } catch (e) {}
          throw new Error(errorBody)
        }

        const data = (await response.json()) as MessagesApiResponse

        if (!data || !Array.isArray(data.messages)) {
          throw new Error('Invalid data structure received from messages API.')
        }

        const mappedMessages: ClientMessage[] = data.messages.map(mapServerMessageToClientMessage)
        setMessagesInStore(convId, mappedMessages, data.nextToken)
      } catch (e) {
        const fetchError = e instanceof Error ? e : new Error(String(e))
        setError(fetchError)
      } finally {
        setIsLoading(false)
      }
    },
    [isLoading, credentialsLoading, getValidCredentials, applicationId, setMessagesInStore]
  )

  useEffect(() => {
    if (conversationId !== fetchAttemptedForId) {
      setFetchAttemptedForId(null)
      setError(null)
    }

    if (!conversationId || credentialsLoading || fetchAttemptedForId === conversationId) {
      return
    }

    const conversation = getConversation(conversationId)

    if (
      conversation &&
      conversation.messages.length === 0 &&
      conversation.messagesNextToken === null
    ) {
      setFetchAttemptedForId(conversationId)
      fetchMessages(conversationId)
    }
  }, [conversationId, getConversation, fetchMessages, credentialsLoading, fetchAttemptedForId])

  return { isLoading, error, fetchMessagesForCurrentId: fetchMessages }
}
